import React, { useMemo, useEffect, useState } from "react";

import { Grid } from "@mui/material";
import { Search } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { Roles, User } from "../../../../../../../../models/user";
import RoleService from "../../../../../../../../services/role.service";
import { useAppState } from "../../../../../../../../context/StateContext";
import Button, { Color } from "../../../../../../../../components/Form/Button";
import Input from "../../../../../../../../components/Form/Input";
import RadioGroup from "../../../../../../../../components/Form/RadioGroup";
import Modal from "../../../../../../../../components/Generic/Modal";
import SkeletonComponent from "../../../../../../../../components/Generic/Skeleton";
import useStyles from "./styles";

interface Props {
  selectedUser?: User;
  isOpen: boolean;
  isLoadingUpdate: boolean;
  closeModal: () => void;
  updateUser: (roleId: number | null, userId: number, user: User) => void;
}

export const AssignedRoleToUserModal = ({
  selectedUser,
  isLoadingUpdate,
  isOpen,
  closeModal,
  updateUser,
}: Props) => {
  const classes = useStyles();
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const [inputValue, setInputValue] = useState<string>("");
  const [rolesList, setRolesList] = useState<Roles[]>();
  const [filterRoles, setFilterRoles] = useState<Roles[]>([]);
  const [roleSelect, setRoleSelect] = useState<Roles | null>();
  const [hasAssignButtonBeClicked, setHasAssignButtonBeClicked] = useState<boolean>(false);

  const setFilterRolesBySearch = () => {
    const filterData = rolesList
      ?.map((role) => role.name.toLowerCase().includes(inputValue.toLowerCase()) && role)
      .filter((item) => typeof item !== "boolean");

    if (!filterData?.includes(false)) setFilterRoles(filterData as Roles[]);
  };

  const getRoleListFilter = () => {
    if (inputValue === "" && rolesList) setFilterRoles(rolesList);
    else setFilterRolesBySearch();
  };

  const setRoleOptions = useMemo(
    () =>
      filterRoles.map((role) => ({
        label: role.name,
        value: role.id,
        "data-cy": role.name,
      })),
    [filterRoles]
  );

  const { isFetching: isFetchingRoles } = useQuery(
    ["roles.list"],
    () =>
      RoleService.getAll({
        status: "Active",
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        setRolesList(response.data);
        setFilterRoles(response.data);
      },
    }
  );

  useEffect(() => {
    if (selectedUser) {
      const role = rolesList?.find((role: Roles) => role.id === selectedUser?.userCompany?.roleId);

      if (role) setRoleSelect(role);
    }
  }, [rolesList, selectedUser]);

  useEffect(() => {
    if (!isLoadingUpdate && hasAssignButtonBeClicked) closeModal();
  }, [isLoadingUpdate]);

  return (
    <Modal
      className={classes.paper}
      data-cy="assignRoleToUserModal"
      open={isOpen}
      handleClose={closeModal}
      title={translate("assignRole")}
      showActionButtons
      actionButtonProps={{
        textSave: translate("assign"),
        dataCySave: "saveButton",
        loading: isLoadingUpdate,
        saveDisabled: !roleSelect,
        onSave: () => {
          if (selectedUser) updateUser(roleSelect?.id || null, selectedUser?.id, selectedUser);
          if (!hasAssignButtonBeClicked) setHasAssignButtonBeClicked(true);
        },
      }}
    >
      <section className={classes.modalSection}>
        <Grid className="mb-3" container direction="row" alignItems="center" spacing={1}>
          <Grid item md={10} lg={10}>
            <Input
              value={inputValue}
              disabled={isLoadingUpdate}
              name="autosuggest"
              placeholder={translate("searchForRole")}
              type="text"
              rightIcon={<Search />}
              onChange={(event) => {
                setInputValue(event.target.value);
                if (event.target.value === "" && rolesList) {
                  setFilterRoles(rolesList);
                }
              }}
              onKeyDown={(keyPress) =>
                keyPress.key === "Enter" && inputValue.length > 0 && getRoleListFilter()
              }
            />
          </Grid>
          <Grid item container md={2} lg={2} justifyContent="flex-end">
            <Button
              className="w-100"
              loading={isLoadingUpdate}
              disabled={isLoadingUpdate || inputValue.length === 0}
              customColor={Color.SECONDARY}
              onClick={() => getRoleListFilter()}
            >
              {translate("search")}
            </Button>
          </Grid>
        </Grid>

        <section className="px-2">
          <p className={classes.modalBodyTitle}>{translate("roles")}</p>
          <SkeletonComponent isLoading={isFetchingRoles}>
            <section data-cy="listOfUserSection">
              {filterRoles?.length === 0 && (
                <p className={classes.notFoundMessage}>{translate("roleNotFound")}</p>
              )}
              {filterRoles && (
                <RadioGroup
                  isDirectionRow={false}
                  value={roleSelect?.id}
                  onChange={(event) => {
                    const role = filterRoles.find(
                      (roles) => Number(roles.id) === Number(event.target.value)
                    );

                    if (role) setRoleSelect(role);
                  }}
                  options={setRoleOptions}
                />
              )}
            </section>
          </SkeletonComponent>
        </section>
      </section>
    </Modal>
  );
};
